import { Typography, Grid, styled } from "@mui/material";
import AcxToggleButton from "components/UI/AcxToggleButton";
import React, { useState } from "react";

const GrayTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.gray[500],
    fontSize: "14px",
    lineHeight: "20px",
}));

interface AgreementMatrixProps {
    labels: string[];
    getAgreementCountAndPercent: (
        humanLabel: string,
        modelLabel: string,
    ) => [number, number];
}

export function AgreementMatrix(props: AgreementMatrixProps) {
    const [renderFormat, setRenderFormat] = useState<"%" | "#">("#");

    return (
        <Grid container direction="column" rowGap={1} marginTop={2}>
            <Grid
                container
                item
                justifyContent="center"
                alignItems="center"
                position="relative"
            >
                <Grid item>
                    <GrayTypography fontWeight={700}>Model</GrayTypography>
                </Grid>
                <Grid item position="absolute" right={0}>
                    <AcxToggleButton
                        id="agreement-matrix-toggle"
                        items={["%", "#"]}
                        shrink
                        onChange={(item) => {
                            if (!item) return;
                            setRenderFormat(item);
                        }}
                        exclusive
                        defaultValue={renderFormat}
                        sx={(theme) => ({
                            height: "36px",
                        })}
                    />
                </Grid>
            </Grid>
            <Grid
                container
                item
                columnGap={1}
                flexWrap="nowrap"
                alignItems="center"
            >
                <Grid item>
                    <GrayTypography
                        fontWeight={700}
                        sx={{ writingMode: "sideways-lr" }}
                    >
                        Labeled
                    </GrayTypography>
                </Grid>
                <Grid
                    container
                    item
                    display="grid"
                    gridTemplateColumns={`0.1fr ${Array.from({
                        length: props.labels.length,
                    })
                        .fill("1fr")
                        .join(" ")}`}
                    gap={1}
                    alignItems="center"
                >
                    <Grid item></Grid>
                    {props.labels.map((modelLabel) => (
                        <Grid item flexGrow={1}>
                            <GrayTypography textAlign="center">
                                {modelLabel}
                            </GrayTypography>
                        </Grid>
                    ))}
                    {props.labels.map((humanLabel, row) => (
                        <React.Fragment>
                            <Grid item flexGrow={1}>
                                <GrayTypography textAlign="right">
                                    {humanLabel}
                                </GrayTypography>
                            </Grid>
                            {props.labels.map((modelLabel, column) => {
                                // diagonal on the matrix means we had agreement
                                const isInAgreement = row === column;
                                const [count, percent] =
                                    props.getAgreementCountAndPercent(
                                        humanLabel,
                                        modelLabel,
                                    );

                                if (count === null || percent === null)
                                    return null;

                                return (
                                    <Grid
                                        item
                                        padding={1}
                                        borderRadius={1}
                                        sx={(theme) => ({
                                            background: isInAgreement
                                                ? theme.palette.primary[800]
                                                : theme.palette.gray[200],
                                        })}
                                    >
                                        <Typography
                                            textAlign="center"
                                            sx={(theme) => ({
                                                color: isInAgreement
                                                    ? theme.palette.white.main
                                                    : "initial",
                                            })}
                                        >
                                            {renderFormat === "#"
                                                ? count
                                                : `${
                                                      percent > 0
                                                          ? percent.toFixed(1)
                                                          : percent
                                                  }%`}
                                        </Typography>
                                    </Grid>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </Grid>
            </Grid>
        </Grid>
    );
}
